import { CallbacksMap } from '@shared/src/utils/CallbacksMap';

type State = {
  id: number;
  type: 'image' | 'video' | null;
  src: string | null;
  file_id: string | null;
  css: string | null;
  color: string | null;
};

export class ReactiveWallpaper {
  private readonly _onPropertyChangeCallbacksMap = new CallbacksMap<keyof State, [any, any]>();

  private readonly _state: State;

  constructor(id: number, initial: Partial<Omit<State, 'id'>> = {}) {
    this._state = {
      id,
      src: initial.src || null,
      file_id: initial.file_id || null,
      css: initial.css || null,
      color: initial.color || null,
      type: initial.type || null,
    };
  }

  get state(): Readonly<State> {
    return this._state;
  }

  get id() {
    return this._state.id;
  }

  get type() {
    return this._state.type;
  }

  set type(value: State['type']) {
    const prev = this._state.type;
    this._state.type = value;
    prev !== value && this._onPropertyChangeCallbacksMap.invoke('type', value, prev);
  }

  get src() {
    return this._state.src;
  }

  set src(value: State['src']) {
    const prev = this._state.src;
    this._state.src = value;
    prev !== value && this._onPropertyChangeCallbacksMap.invoke('src', value, prev);
  }

  get file_id() {
    return this._state.file_id;
  }

  set file_id(value: State['file_id']) {
    const prev = this._state.file_id;
    this._state.file_id = value;
    prev !== value && this._onPropertyChangeCallbacksMap.invoke('file_id', value, prev);
  }

  get css() {
    return this._state.css;
  }

  set css(value: State['css']) {
    const prev = this._state.css;
    this._state.css = value;
    prev !== value && this._onPropertyChangeCallbacksMap.invoke('css', value, prev);
  }

  get color() {
    return this._state.color;
  }

  set color(value: State['color']) {
    const prev = this._state.color;
    this._state.color = value;
    prev !== value && this._onPropertyChangeCallbacksMap.invoke('color', value, prev);
  }

  onPropertyChange = <TKey extends keyof State>(
    key: TKey,
    callback: (next: State[TKey], prev: State[TKey]) => void
  ) => {
    this._onPropertyChangeCallbacksMap.set(key, callback);
  };

  clear = () => {
    this.src = null;
    this.type = null;
    this.file_id = null;
    this.css = null;
    this.color = null;
  };
}
